import React, { useState, useEffect } from "react";
import { useModalForm } from "../../hooks/useModalForm";
import FieldError from "../fields/FieldError";
import Label from "../fields/Label";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { BiaFitUser } from "../../types/user";
import { validateFields, validationRules } from "../../utils/validation";
import { useAuth } from "contexts/AuthContext";
import ModalShell from "../modals/ModalShell";
import Button from "../common/Button";
import { logger } from "../../utils/logger";

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    userId: number,
    password: string,
    passwordConfirmation: string
  ) => Promise<void>;
  user: BiaFitUser | null;
  loading?: boolean;
  error?: string | null;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  user,
  loading = false,
  error: responseError,
}) => {
  const { user: loggedInUser } = useAuth();
  const isMyProfile = loggedInUser && user && loggedInUser.id === user.id;

  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const {
    values: formData,
    setValue: handleInputChange,
    errors,
    isSubmitting,
    handleSubmit,
  } = useModalForm({
    isOpen,
    initialValues: { password: "", repeat_password: "" },
    validate: (v) => {
      const next = validateFields(v, {
        password: {
          required: true,
          rules: [
            validationRules.password(8),
            validationRules.passwordStrong(),
          ],
        },
      });

      if (!v.repeat_password || v.repeat_password.trim() === "") {
        next.repeat_password = "Please confirm the password";
      } else if (v.password !== v.repeat_password) {
        next.repeat_password = "Passwords do not match";
      }

      return next;
    },
    onSubmit: async (v) => {
      if (!user) return;
      try {
        await onSubmit(user.id, v.password, v.repeat_password);
      } catch (error) {
        logger.error("Error submitting form:", error);
      }
    },
  });

  // The reveal toggles are not form values, so they reset alongside it.
  useEffect(() => {
    if (isOpen) {
      setShowPassword(false);
      setShowRepeatPassword(false);
    }
  }, [isOpen]);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={`Change ${isMyProfile ? "My" : "User"} Password`}
      size="md"
      layout="fixed"
      closeDisabled={isSubmitting}
      message={responseError ? { text: responseError, type: "error" } : null}
      onSubmit={handleSubmit}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loadingLabel="Changing..."
            disabled={loading}
          >
            Change Password
          </Button>
        </>
      }
    >
      <div className="p-6">
        {!isMyProfile && user && (
          <div className="mb-4 rounded-md bg-gray-50 p-3 dark:bg-navy-700">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Changing password for:{" "}
              <span className="font-semibold">
                {user.first_name} {user.last_name}
              </span>
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {user.email}
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Label>New Password *</Label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                className={`w-full rounded-md border px-3 py-2 pr-10 shadow-sm focus:border-biafit-primary focus:outline-none focus:ring-2 focus:ring-biafit-primary dark:border-gray-600 dark:bg-navy-700 dark:text-white ${
                  errors.password ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Enter new password"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? (
                  <MdVisibilityOff className="h-5 w-5" />
                ) : (
                  <MdVisibility className="h-5 w-5" />
                )}
              </button>
            </div>
            <FieldError>{errors.password}</FieldError>
          </div>

          <div>
            <Label>Confirm Password *</Label>
            <div className="relative">
              <input
                type={showRepeatPassword ? "text" : "password"}
                value={formData.repeat_password}
                onChange={(e) =>
                  handleInputChange("repeat_password", e.target.value)
                }
                className={`w-full rounded-md border px-3 py-2 pr-10 shadow-sm focus:border-biafit-primary focus:outline-none focus:ring-2 focus:ring-biafit-primary dark:border-gray-600 dark:bg-navy-700 dark:text-white ${
                  errors.repeat_password ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="Confirm new password"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showRepeatPassword ? (
                  <MdVisibilityOff className="h-5 w-5" />
                ) : (
                  <MdVisibility className="h-5 w-5" />
                )}
              </button>
            </div>
            <FieldError>{errors.repeat_password}</FieldError>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

export default ChangePasswordModal;
